/**
 * Axios グローバル設定
 *
 * @file axios.ts
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * axios全体にグローバルインターセプターを設定。
 * JWT認証とCSRF対策を全リクエストに自動適用。
 */

import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { getCsrfToken, clearCsrfToken } from '../utils/csrf';
import { handleUnauthorized } from '../utils/authRedirect';

// API用のaxiosインスタンスを作成
const API_BASE = import.meta.env.VITE_API_BASE_URL;

const axiosInstance = axios.create({
  baseURL: `${API_BASE}/api/v1`,
  withCredentials: true,
});

// リクエストインターセプター
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // CSRF対策: POST/PUT/DELETEリクエストにCSRFトークン追加
    const mutatingMethods = ['post', 'put', 'delete', 'patch'];
    if (config.method && mutatingMethods.includes(config.method.toLowerCase())) {
      try {
        // roleを動的に判定（ホスト名やポート番号から推測）
        const hostname = window.location.hostname;
        const port = window.location.port;
        let role: 'buyer' | 'seller' | 'admin' = 'buyer'; // デフォルト

        if (hostname.includes('admin') || port === '3005' || port === '7005' || port === '8005') {
          role = 'admin';
        } else if (hostname.includes('seller') || port === '3004' || port === '7004' || port === '8004') {
          role = 'seller';
        }

        const csrfToken = await getCsrfToken(role);
        config.headers['X-CSRF-Token'] = csrfToken;
      } catch {
        // トークン取得失敗時もリクエストは続行（バックエンドで403エラーとなる）
      }
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// レスポンスインターセプター（エラーハンドリング）
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    // 401 Unauthorized: セッション切れ -> ログイン画面へリダイレクト
    if (error.response?.status === 401) {
      handleUnauthorized(error.config?.url);
    }

    // CSRF検証失敗時のハンドリング
    if (error.response?.status === 403) {
      const errorData = error.response?.data as { detail?: string };
      const errorMessage = errorData?.detail || '';
      if (errorMessage.includes('CSRF')) {
        console.error('CSRF検証エラー: トークンを再取得します');
        // CSRFトークンをクリアして再取得を促す
        clearCsrfToken();
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
